export const RESET_MS = 1000

export function matchingText(option) {
  const explicit = option.dataset.label
  if (explicit != null && explicit.trim() !== "") {
    return explicit.trim()
  }

  return option.textContent.trim()
}

export function normalizeForMatch(string) {
  return string.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
}

/**
 * Invisible prefix type-ahead for a select. The controller supplies the
 * current enabled options, the current option, and what to do on a match
 * (commit when closed, highlight when open).
 */
export class Typeahead {
  constructor({
    getOptions,
    getCurrent,
    onMatch,
    isEnabled = () => true,
    resetMs = RESET_MS
  }) {
    this.getOptions = getOptions
    this.getCurrent = getCurrent
    this.onMatch = onMatch
    this.isEnabled = isEnabled
    this.resetMs = resetMs
    this.buffer = ""
    this.sessionStart = null
    this.lastMatch = null
    this.timer = null
  }

  get active() {
    return this.buffer !== ""
  }

  stop() {
    this.clearSession()
  }

  reset() {
    this.clearSession()
  }

  // True when the key was consumed as type-ahead (caller should not also
  // treat it as open/select/navigate).
  handleKey(event) {
    if (!this.isEnabled()) return false
    if (event.ctrlKey || event.altKey || event.metaKey) return false

    const { key } = event
    if (key === " " && !this.active) return false
    if (key.length !== 1) return false

    event.preventDefault()
    this.applyCharacter(key)
    return true
  }

  applyCharacter(key) {
    const options = this.getOptions()
    const isNewSession = this.buffer === ""

    if (isNewSession) {
      this.sessionStart = this.getCurrent() ?? null
    }

    if (this.shouldCycle(options, key)) {
      this.buffer = ""
      this.sessionStart = this.lastMatch
    }

    this.buffer += key
    this.restartTimer()

    const match = this.findMatch(options, this.buffer, this.sessionStart)
    if (match) {
      this.lastMatch = match
      this.onMatch(match)
      return
    }

    if (key !== " ") {
      this.clearSession()
    }
  }

  shouldCycle(options, key) {
    if (!this.cycleEligible(options)) return false
    if (this.buffer === "") return false

    return normalizeForMatch(this.buffer) === normalizeForMatch(key)
  }

  cycleEligible(options) {
    return options.every(option => {
      const text = normalizeForMatch(matchingText(option))
      if (text.length < 2) return true

      return text[0] !== text[1]
    })
  }

  findMatch(options, buffer, startAfter) {
    if (options.length === 0) return null

    let startIndex = options.indexOf(startAfter)
    if (startAfter == null || startIndex === -1) {
      startIndex = -1
    }

    const needle = normalizeForMatch(buffer)
    for (let offset = 0; offset < options.length; offset += 1) {
      const index = (startIndex + 1 + offset) % options.length
      const haystack = normalizeForMatch(matchingText(options[index]))
      if (haystack.startsWith(needle)) return options[index]
    }

    return null
  }

  restartTimer() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.clearSession(), this.resetMs)
  }

  clearSession() {
    clearTimeout(this.timer)
    this.timer = null
    this.buffer = ""
    this.sessionStart = null
    this.lastMatch = null
  }
}
